// PAT-14 — account settings form (embedded in chat layout).
import { Component, OnInit, inject, input, output, computed, signal, effect } from '@angular/core';
import { IonSpinner } from '@ionic/angular/standalone';
import { AuthService } from '../../services/auth.service';
import { ToastFeedbackService } from '../../services/toast-feedback.service';
import { AccountSettingsDialogService } from '../../services/account-settings-dialog.service';
import { ThemeService, ThemePreference } from '../../services/theme.service';
import { FontSizeService, FontSizePreference } from '../../services/font-size.service';
import {
  AccountPreferencesService,
  AccountPreferences,
  DefaultModelSelection,
  FILE_UPLOAD_TYPE_OPTIONS,
} from '../../services/account-preferences.service';
import { ModelCatalogService } from '../../services/model-catalog.service';
import {
  buildDefaultModelPickerLabel,
  catalogModelsExcludingDefaultFallback,
  DEFAULT_MODEL_CATALOG_KEY,
} from '../../utils/default-model-label.util'; // PAT-495 / PAT-723
import { UserMenuNavigationLoadingService } from '../../services/user-menu-navigation-loading.service';
import { WorkIqService } from '../../services/work-iq.service';
import { environment } from '../../../environments/environment';

function snapshotsEqual(a: AccountPreferences, b: AccountPreferences): boolean {
  const sort = (xs: string[]) => [...xs].sort().join(',');
  const identity =
    environment.ssoEnabled ?
      true
      : a.firstName.trim() === b.firstName.trim() &&
      a.lastName.trim() === b.lastName.trim();
  return (
    identity &&
    a.defaultModel.modelKey === b.defaultModel.modelKey &&
    sort(a.fileUploadTypeIds) === sort(b.fileUploadTypeIds)
  );
}

@Component({
  selector: 'app-account-settings',
  standalone: true,
  imports: [IonSpinner],
  templateUrl: './account-settings.component.html',
  styleUrl: './account-settings.component.scss',
})
export class AccountSettingsComponent implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly prefsSvc = inject(AccountPreferencesService);
  private readonly modelCatalog = inject(ModelCatalogService);
  private readonly toastFeedback = inject(ToastFeedbackService);
  private readonly settingsDialog = inject(AccountSettingsDialogService);
  private readonly userMenuNavLoading = inject(UserMenuNavigationLoadingService);
  readonly workIq = inject(WorkIqService);
  readonly themeService = inject(ThemeService);
  readonly fontSizeService = inject(FontSizeService);

  /** PAT-335 — Connected accounts (Microsoft / workIQ); only when feature flag on. */
  readonly workIqEnabled = environment.workIqEnabled === true;
  workIqStatusLoading = signal(false);
  workIqStatusError = signal<string | null>(null);
  /** PAT-338 — Phase B verification harness (not product UI). */
  workIqProbeBusy = signal(false);
  workIqProbeOk = signal(false);
  /** PAT-340 — first product action (search / summary) in Account Settings. */
  workIqActionQuery = signal('');
  workIqActionBusy = signal(false);
  workIqActionError = signal<string | null>(null);
  workIqActionResult = signal<string | null>(null);
  workIqLastAction = signal<'search' | 'summary' | null>(null);
  /**
   * Bumped on disconnect (and when starting a new action) so an in-flight
   * search/summary cannot apply stale results after connection changes.
   */
  private workIqActionGeneration = 0;

  /**
   * PAT-340 — single UI busy gate: connect/disconnect, status refresh, and
   * search/summary must not run concurrently (avoids stale results / status).
   */
  readonly workIqUiBusy = computed(
    () =>
      this.workIq.actionBusy() ||
      this.workIqActionBusy() ||
      this.workIqStatusLoading(),
  );

  /** PAT-203 — draft appearance values; applied on Save only. */
  draftThemePreference = signal<ThemePreference>('light');
  draftFontSize = signal<FontSizePreference>('m');
  private appearanceBaseline = signal<{
    theme: ThemePreference;
    fontSize: FontSizePreference;
  } | null>(null);

  /** PAT-203 — appearance tile options (persisted locally on Save). */
  readonly themeOptions: { id: ThemePreference; label: string; icon: string }[] = [
    { id: 'light', label: 'Light', icon: 'far fa-lightbulb' },
    { id: 'dark', label: 'Dark', icon: 'fas fa-lightbulb' },
    { id: 'system', label: 'System', icon: 'fa-solid fa-circle-half-stroke' },
  ];
  readonly fontSizeOptions: { id: FontSizePreference; label: string }[] = [
    { id: 's', label: 'S' },
    { id: 'm', label: 'M' },
    { id: 'l', label: 'L' },
  ];

  /** PAT-203 — when true, form is hosted inside the settings modal. */
  dialogMode = input(false);
  activeTab = input<'profile' | 'preferences' | 'appearance'>('profile');

  /** Theme + font tiles — Appearance tab only (localStorage; not part of account prefs save). */
  showAppearance = computed(
    () => !this.dialogMode() || this.activeTab() === 'appearance',
  );

  /** Profile / model prefs form (server-backed). */
  showAccountPrefsForm = computed(
    () =>
      !this.dialogMode() ||
      this.activeTab() === 'profile' ||
      this.activeTab() === 'preferences',
  );
  requestClose = output<void>();

  /** PAT-14 — identity comes from IdP when SSO is enabled; fields are not editable. */
  readonly identityFieldsDisabled = environment.ssoEnabled;

  readonly fileUploadOptions = FILE_UPLOAD_TYPE_OPTIONS;

  firstName = signal('');
  lastName = signal('');
  emailDisplay = signal('');
  defaultModelKey = signal('auto');
  fileUploadTypeIds = signal<string[]>([]);

  private baseline = signal<AccountPreferences | null>(null);

  saveError = signal<string | null>(null);

  /** True until GET /models + account prefs sync complete and form fields are set. */
  initialLoading = signal(true);

  modelLoadError = computed(() =>
    this.modelCatalog.loadState() === 'error' ? this.modelCatalog.loadError() : null,
  );

  modelsLoading = computed(
    () =>
      this.modelCatalog.loadState() === 'idle' ||
      this.modelCatalog.loadState() === 'loading',
  );

  /**
   * Dropdown options: Default (`auto`), catalog models (excluding the fallback key — PAT-723),
   * and — when the saved default is not in the catalog — a row for that key so the native select
   * shows the persisted choice (e.g. Gemini in DB while GET /models lists another provider first).
   */
  modelRows = computed(() => {
    const catalog = this.modelCatalog.activeModels();
    const catalogKeys = new Set(catalog.map(m => m.modelKey));
    // PAT-723 — Default row already represents the fallback catalog model.
    const active = catalogModelsExcludingDefaultFallback(catalog).map(m => ({
      id: m.modelKey,
      label: m.displayName,
    }));
    const key = (this.defaultModelKey() || 'auto').trim() || 'auto';
    const orphan: { id: string; label: string }[] = [];
    if (
      key !== 'auto' &&
      key !== DEFAULT_MODEL_CATALOG_KEY &&
      !catalogKeys.has(key)
    ) {
      const dm = this.prefsSvc.preferences()?.defaultModel;
      let label = key;
      if (
        dm &&
        dm.modelKey === key &&
        dm.displayName?.trim()
      ) {
        label = dm.providerDisplayName?.trim()
          ? `${dm.displayName} (${dm.providerDisplayName})`
          : dm.displayName;
      }
      orphan.push({ id: key, label });
    }
    return [
      { id: 'auto', label: buildDefaultModelPickerLabel(catalog) },
      ...orphan,
      ...active,
    ];
  });

  /** Portal role from session/JWT (`AuthUser.role`); display-only on this screen. */
  readonly roleDisplayLabel = computed(() => {
    const r = this.auth.user()?.role;
    if (!r) return '—';
    return r === 'admin' ? 'Administrator' : 'Standard user';
  });

  hasUnsavedChanges = computed(() => {
    const b = this.baseline();
    const prefsDirty = b ? !snapshotsEqual(this.captureSnapshot(), b) : false;
    return prefsDirty || this.hasAppearanceChanges();
  });

  hasAppearanceChanges = computed(() => {
    const b = this.appearanceBaseline();
    if (!b) return false;
    return (
      this.draftThemePreference() !== b.theme ||
      this.draftFontSize() !== b.fontSize
    );
  });

  dialogHeadingTitle = computed(() => {
    switch (this.activeTab()) {
      case 'profile':
        return 'Profile';
      case 'appearance':
        return 'Appearance';
      default:
        return 'Preferences';
    }
  });

  dialogHeadingIntro = computed(() => {
    switch (this.activeTab()) {
      case 'profile':
        return 'Your identity and role information.';
      case 'appearance':
        return 'Theme and font size for the app interface. Saved to your account when you save.';
      default:
        return 'Default model and file upload preferences for chat.';
    }
  });

  constructor() {
    effect(() => {
      if (this.dialogMode()) {
        this.settingsDialog.registerDirtyChecker(() => this.hasUnsavedChanges());
        if (this.settingsDialog.active()) {
          this.resetToSavedState();
        }
      } else {
        this.settingsDialog.registerDirtyChecker(null);
      }
    });

    effect(() => {
      if (
        this.dialogMode() &&
        this.settingsDialog.active() &&
        !this.initialLoading()
      ) {
        this.userMenuNavLoading.notifyChildReady();
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.initialLoading.set(true);
    try {
      const email = this.auth.user()?.email ?? '';
      this.emailDisplay.set(email);
      await this.modelCatalog.load();
      await this.prefsSvc.syncFromRemoteIfEnabled();
      const resolved = this.prefsSvc.resolvedPreferences();
      this.applyPrefs(resolved);
      this.baseline.set(resolved);
      this.syncAppearanceDraftsFromApplied();
      await this.loadWorkIqStatus();
    } finally {
      this.initialLoading.set(false);
    }
  }

  /** PAT-335 / PAT-337 — server-backed Microsoft connection status for Profile tab. */
  private async loadWorkIqStatus(): Promise<void> {
    if (!this.workIqEnabled) return;
    this.workIqStatusLoading.set(true);
    this.workIqStatusError.set(null);
    try {
      await this.workIq.refreshStatus();
      // Probe success is session-bound to the current connection (PAT-338).
      if (!this.workIq.isConnected()) {
        this.workIqProbeOk.set(false);
      }
    } catch {
      // Calm error only — never force Pathways SSO relogin (PAT-337 edge case).
      this.workIq.clearConnectionStatus();
      this.workIqProbeOk.set(false);
      this.workIqStatusError.set('Unable to load Microsoft connection status.');
    } finally {
      this.workIqStatusLoading.set(false);
    }
  }

  async onConnectMicrosoft(): Promise<void> {
    if (!this.workIqEnabled || this.workIqUiBusy()) return;
    this.workIqStatusError.set(null);
    // New connection has not been verified — do not keep prior probe success (PAT-338).
    this.workIqProbeOk.set(false);
    try {
      await this.workIq.connectMicrosoft();
      await this.loadWorkIqStatus();
      this.toastFeedback.show('Microsoft account connected.');
    } catch {
      this.workIqStatusError.set(
        'Unable to connect Microsoft account. Try again or contact support.',
      );
    }
  }

  async onDisconnectMicrosoft(): Promise<void> {
    if (!this.workIqEnabled || this.workIqUiBusy()) return;
    this.workIqStatusError.set(null);
    // Invalidate any in-flight search/summary before clearing connection.
    this.workIqActionGeneration += 1;
    this.workIqActionBusy.set(false);
    this.workIqActionResult.set(null);
    this.workIqActionError.set(null);
    this.workIqLastAction.set(null);
    try {
      await this.workIq.disconnectMicrosoft();
      this.workIqProbeOk.set(false);
      this.toastFeedback.show('Microsoft account disconnected.');
    } catch {
      this.workIqStatusError.set('Unable to disconnect Microsoft account.');
    }
  }

  /**
   * PAT-338 — minimal trigger for WorkIqService.query (dual-auth smoke).
   * Does not invent product chat UI; success/fail only (no response body in UI).
   * In-flight guard is synchronous (check + set) so rapid clicks cannot start
   * overlapping queries before the template [disabled] updates.
   */
  async onVerifyWorkIqQuery(): Promise<void> {
    if (!this.workIqEnabled || !this.workIq.isConnected() || this.workIqProbeBusy()) {
      return;
    }
    this.workIqProbeBusy.set(true);
    this.workIqProbeOk.set(false);
    this.workIqStatusError.set(null);
    try {
      await this.workIq.query('Pathways workIQ connectivity check');
      this.workIqProbeOk.set(true);
      this.toastFeedback.show('workIQ query succeeded.');
    } catch (err) {
      this.workIqProbeOk.set(false);
      const message =
        err instanceof Error && err.message.trim()
          ? err.message
          : 'workIQ request failed.';
      // Suppress only when THIS failure is workIQ/MSAL auth (banner covers it).
      // Do not key off sticky needsReconnect() — a prior 401 would hide later 502/503 (PAT-338).
      if (
        /Microsoft authorization expired|Microsoft re-authentication required/i.test(
          message,
        )
      ) {
        return;
      }
      this.workIqStatusError.set(message);
    } finally {
      this.workIqProbeBusy.set(false);
    }
  }

  /**
   * PAT-340 — run search or summary via Orchestrator.
   * Not connected → prompt only (no silent MSAL redirect).
   * 401 → reconnect banner; Pathways SSO stays intact.
   * Mutual exclusion with connect/disconnect via workIqUiBusy.
   */
  async onRunWorkIqAction(action: 'search' | 'summary'): Promise<void> {
    if (!this.workIqEnabled || this.workIqUiBusy()) return;
    this.workIqActionError.set(null);
    this.workIqActionResult.set(null);

    if (!this.workIq.isConnected()) {
      // AC2 — stay on Account Settings; do not call connectMicrosoft() implicitly.
      this.workIqActionError.set(
        'Connect your Microsoft account above before running a workIQ action.',
      );
      return;
    }

    const query = this.workIqActionQuery().trim();
    if (!query) {
      this.workIqActionError.set('Enter a topic to search or summarize.');
      return;
    }

    const generation = ++this.workIqActionGeneration;
    this.workIqActionBusy.set(true);
    this.workIqLastAction.set(action);
    try {
      const result = await this.workIq.runAction(action, query);
      if (generation !== this.workIqActionGeneration) {
        return;
      }
      this.workIqActionResult.set(result.message);
    } catch (err) {
      if (generation !== this.workIqActionGeneration) {
        return;
      }
      const message =
        err instanceof Error && err.message.trim()
          ? err.message
          : 'workIQ request could not be completed. Try again later.';
      this.workIqActionError.set(message);
    } finally {
      if (generation === this.workIqActionGeneration) {
        this.workIqActionBusy.set(false);
      }
    }
  }

  onWorkIqQueryInput(event: Event): void {
    const el = event.target as HTMLTextAreaElement | null;
    this.workIqActionQuery.set(el?.value ?? '');
  }

  private defaultModelFromKey(key: string): DefaultModelSelection {
    const k = (key || 'auto').trim() || 'auto';
    if (k === 'auto') {
      return {
        modelKey: 'auto',
        displayName: buildDefaultModelPickerLabel(this.modelCatalog.activeModels()),
        providerDisplayName: '',
      };
    }
    const m = this.modelCatalog.activeModels().find(x => x.modelKey === k);
    return {
      modelKey: k,
      displayName: m?.displayName ?? k,
      providerDisplayName: m?.providerDisplayName ?? '',
    };
  }

  private captureSnapshot(): AccountPreferences {
    return {
      firstName: this.firstName().trim(),
      lastName: this.lastName().trim(),
      defaultModel: this.defaultModelFromKey(this.defaultModelKey()),
      fileUploadTypeIds: [...this.fileUploadTypeIds()],
    };
  }

  private applyPrefs(p: AccountPreferences): void {
    this.firstName.set(p.firstName);
    this.lastName.set(p.lastName);
    // PAT-723 — explicit fallback key is the same choice as Default in the picker.
    const rawKey = (p.defaultModel.modelKey || 'auto').trim() || 'auto';
    this.defaultModelKey.set(rawKey === DEFAULT_MODEL_CATALOG_KEY ? 'auto' : rawKey);
    this.fileUploadTypeIds.set([...p.fileUploadTypeIds]);
  }

  toggleFileType(id: string): void {
    this.fileUploadTypeIds.update(ids => {
      const set = new Set(ids);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return [...set];
    });
  }

  isFileTypeChecked(id: string): boolean {
    return this.fileUploadTypeIds().includes(id);
  }

  selectTheme(preference: ThemePreference): void {
    this.draftThemePreference.set(preference);
  }

  selectFontSize(size: FontSizePreference): void {
    this.draftFontSize.set(size);
  }

  /** Revert account prefs and appearance drafts after discard or dialog reopen. */
  revertUnsavedChanges(): void {
    this.resetToSavedState();
    this.saveError.set(null);
  }

  private resetToSavedState(): void {
    const b = this.baseline();
    if (b) this.applyPrefs(b);
    this.syncAppearanceDraftsFromApplied();
  }

  private syncAppearanceDraftsFromApplied(): void {
    const theme = this.themeService.themePreference();
    const fontSize = this.fontSizeService.fontSize();
    this.draftThemePreference.set(theme);
    this.draftFontSize.set(fontSize);
    this.appearanceBaseline.set({ theme, fontSize });
  }

  private applyAppearanceDrafts(): void {
    this.themeService.setThemePreference(this.draftThemePreference());
    this.fontSizeService.setFontSize(this.draftFontSize());
    this.appearanceBaseline.set({
      theme: this.draftThemePreference(),
      fontSize: this.draftFontSize(),
    });
  }

  onFormSubmit(ev: Event): void {
    ev.preventDefault();
    void this.save();
  }

  async save(): Promise<void> {
    this.saveError.set(null);
    if (!this.auth.user()) {
      this.auth.forceSsoRelogin();
      return;
    }

    const snapshot = this.captureSnapshot();
    const b = this.baseline();
    const prefsDirty = !b || !snapshotsEqual(snapshot, b);
    const appearanceDirty = this.hasAppearanceChanges();

    if (!prefsDirty && !appearanceDirty) {
      return;
    }

    let first = snapshot.firstName;
    let last = snapshot.lastName;
    if (prefsDirty && !environment.ssoEnabled) {
      if (!first) {
        this.saveError.set('First name is required.');
        return;
      }
      if (!last) {
        this.saveError.set('Last name is required.');
        return;
      }
    } else if (environment.ssoEnabled && prefsDirty) {
      const resolved = this.prefsSvc.resolvedPreferences();
      first = resolved.firstName.trim();
      last = resolved.lastName.trim();
    }

    const prefsPayload: AccountPreferences = {
      firstName: first,
      lastName: last,
      defaultModel: snapshot.defaultModel,
      fileUploadTypeIds: snapshot.fileUploadTypeIds,
    };

    try {
      if (appearanceDirty) {
        this.applyAppearanceDrafts();
      }
      const appearancePayload = {
        themePreference: this.themeService.themePreference(),
        fontSize: this.fontSizeService.fontSize(),
      };
      if (prefsDirty || appearanceDirty) {
        await this.prefsSvc.save(
          prefsDirty
            ? prefsPayload
            : {
              firstName: snapshot.firstName,
              lastName: snapshot.lastName,
              defaultModel: snapshot.defaultModel,
              fileUploadTypeIds: snapshot.fileUploadTypeIds,
            },
          appearancePayload,
        );
        if (prefsDirty) {
          this.baseline.set(prefsPayload);
        }
      }
      // PAT-334 — bottom-only toast (position overrides ignored by ToastFeedbackService).
      await this.toastFeedback.show('Your settings were saved.', {
        variant: 'success',
        duration: 2800,
      });
    } catch {
      this.saveError.set('Could not save settings. Try again.');
    }
  }

  async retryModels(): Promise<void> {
    await this.modelCatalog.retry();
  }
}
